import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { destinationAt } from '../navCurrent'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import {
  navItemsFor,
  railLimitFor,
  type Destination,
  type NavGroup,
} from '../nav'
import { navSubjectFrom } from '../navSubject'
import { useSession } from '../useSession'
import { iconFor } from './navIcons'
import NavMenu from './NavMenu'
import { useNavBadges } from './useNavBadges'

/**
 * One navigation, in three shapes (D-322, D-323).
 *
 * | Width | Shape |
 * |---|---|
 * | >= 1024px | Labelled rail, grouped |
 * | 640-1023px | Icon rail, no choice offered |
 * | < 640px | Bottom bar, five slots |
 *
 * **Same list, same order, at every width.** A destination that is third on a
 * laptop is third on a phone, or muscle memory is per-device and worth nothing.
 *
 * **Only one shape is ever in the accessibility tree.** Both are rendered and
 * `display: none` removes the other, so a screen reader is never offered the
 * navigation twice — the arrangement the portal tab bar used before this
 * replaced it.
 */
export default function NavRail() {
  const { session } = useSession()
  const [collapsed, setCollapsed] = useCollapsed()
  const badges = useNavBadges()
  const { pathname } = useLocation()

  if (!session) return null

  const items = navItemsFor(navSubjectFrom(session))

  // A kiosk reaches nothing, so there is nothing to draw (D-233).
  if (items.length === 0) return null

  return (
    <>
      <Rail
        items={items}
        badges={badges}
        here={destinationAt(pathname)}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <BottomBar items={items} badges={badges} here={destinationAt(pathname)} />
    </>
  )
}

/**
 * Remembered per device, not per account (D-309, D-323).
 *
 * Which shape suits you is a property of where you are sitting — the same
 * laptop wants the same rail whoever signs in on it.
 */

const KEY = 'cda.nav.collapsed'

function useCollapsed(): [boolean, (v: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(KEY) === 'true'
    } catch {
      // Private browsing throws on read. A rail that fails to open is worse
      // than one that forgets.
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(KEY, String(collapsed))
    } catch {
      /* nothing to do, and nothing worth telling anybody */
    }
  }, [collapsed])

  return [collapsed, setCollapsed]
}

const GROUP_LABELS: Record<NavGroup, string | null> = {
  main: null,
  money: 'Money',
  setup: 'Set up',
}

function Rail({
  items,
  badges,
  here,
  collapsed,
  onToggle,
}: {
  items: Destination[]
  badges: Record<string, number>
  here: string | null
  collapsed: boolean
  onToggle: () => void
}) {
  const groups: NavGroup[] = ['main', 'money', 'setup']

  return (
    <nav
      aria-label="Main"
      className={`hidden shrink-0 flex-col gap-1 border-r border-line bg-surface px-2 py-4 sm:flex ${
        collapsed ? 'w-14' : 'w-14 lg:w-56'
      }`}
    >
      {groups.map((group) => {
        const inGroup = items.filter((d) => d.group === group)
        if (inGroup.length === 0) return null

        const heading = GROUP_LABELS[group]

        return (
          <div key={group} className="flex flex-col gap-0.5">
            {/*
              The groups are positions in one order, not a separate structure -
              so the heading is a label on a stretch of the list, and it goes
              when there is no room to read it.
            */}
            {heading && (
              <p
                /* 11px floor, measured (D-255). 10.5px is what NavMenu uses inside a
                   dropdown; this one is permanent chrome and has to clear it. */
                className={`mt-3 mb-1 px-2 text-[11px] font-bold tracking-[0.12em] text-ink-3 uppercase ${
                  collapsed ? 'sr-only' : 'sr-only lg:not-sr-only'
                }`}
              >
                {heading}
              </p>
            )}

            {inGroup.map((destination) => (
              <RailItem
                key={destination.key}
                destination={destination}
                count={badges[destination.key] ?? 0}
                active={here === destination.key}
                collapsed={collapsed}
              />
            ))}
          </div>
        )
      })}

      {/*
        Offered only where there is a choice to make (D-323). Below 1024px the
        shape follows the width, and a control that does nothing is one people
        report as broken.
      */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand the navigation' : 'Collapse the navigation'}
        className="mt-auto hidden items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-ink-3 hover:bg-surface-2 hover:text-ink lg:flex"
      >
        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        <span className={collapsed ? 'sr-only' : ''}>Collapse</span>
      </button>
    </nav>
  )
}

function RailItem({
  destination,
  count,
  active,
  collapsed,
}: {
  destination: Destination
  count: number
  active: boolean
  collapsed: boolean
}) {
  return (
    <Link
      to={destination.to}
      /*
       * The name is always in the accessibility tree, whatever the width
       * (D-323). `sr-only` hides it from the eye and keeps it for a screen
       * reader; `title` gives a mouse the same answer on hover, and the
       * accessible name gives a keyboard user it on focus.
       */
      title={destination.label}
      aria-current={active ? 'page' : undefined}
      className={[
        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] font-semibold no-underline transition-colors',
        active ? 'bg-accent-soft text-accent-2' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
      ].join(' ')}
    >
      <span className="relative grid shrink-0 place-items-center">
        {iconFor(destination.key)}

        {/*
          A pip when there is no room for the number (D-323). A closed or narrow
          thing can only honestly say *there is something in here*; the count
          appears beside the label, where it has one.
        */}
        {count > 0 && (
          <span
            aria-hidden="true"
            className={`absolute -top-0.5 -right-0.5 size-2 rounded-full bg-accent ring-2 ring-surface ${
              collapsed ? '' : 'lg:hidden'
            }`}
          />
        )}
      </span>

      <span className={collapsed ? 'sr-only' : 'sr-only lg:not-sr-only'}>
        {destination.label}
      </span>

      {count > 0 && (
        <span
          className={`ml-auto rounded-full bg-accent-soft px-1.5 text-[11px] font-bold text-accent-2 tabular-nums ${
            collapsed ? 'sr-only' : 'sr-only lg:not-sr-only'
          }`}
        >
          {count}
        </span>
      )}
    </Link>
  )
}

/**
 * The phone, where five is the ceiling (D-323).
 *
 * A parent and a teacher fit; Tonya does not, and hers is a genuine overflow.
 * A dual-role account is the one non-admin who meets *More*, which is the
 * arithmetic of holding both halves rather than a defect (D-322).
 */
function BottomBar({
  items,
  badges,
  here,
}: {
  items: Destination[]
  badges: Record<string, number>
  here: string | null
}) {
  const limit = railLimitFor('base')
  const fits = items.length <= limit

  // Four plus More, so More is a slot rather than a replacement for one.
  const shown = fits ? items : items.slice(0, limit - 1)
  const rest = fits ? [] : items.slice(limit - 1)

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] sm:hidden"
    >
      {shown.map((destination) => (
        <Link
          key={destination.key}
          to={destination.to}
          aria-current={here === destination.key ? 'page' : undefined}
          className={[
            'flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] font-semibold no-underline',
            here === destination.key ? 'text-accent-2' : 'text-ink-3',
          ].join(' ')}
        >
          <span className="relative grid place-items-center">
            {iconFor(destination.key)}
            {(badges[destination.key] ?? 0) > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-accent ring-2 ring-surface"
              />
            )}
          </span>
          {/* 11px floor, and the label never wraps to a second line (D-255, D-314). */}
          <span className="max-w-full truncate">{destination.label}</span>
        </Link>
      ))}

      {rest.length > 0 && (
        <div className="flex flex-1 items-center justify-center">
          {/* A dot on the closed menu, counts on the items (D-167, D-323). */}
          {/*
            `drop="up"`: this bar is `bottom-0`, so a panel opening downward
            opens off the screen — every item of it (D-337).
          */}
          <NavMenu
            label="More"
            align="right"
            drop="up"
            dot={rest.some((d) => (badges[d.key] ?? 0) > 0)}
            groups={[
              {
                items: rest.map((d) => ({
                  label: d.label,
                  to: d.to,
                  count: badges[d.key] ?? 0,
                })),
              },
            ]}
          />
        </div>
      )}
    </nav>
  )
}
